/**
 * Nexus AI relationship suggestions — accept or dismiss pending proposals.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw, Search } from 'lucide-react';
import { getAppDefinition } from '../registry';
import { AppShell, BallLine, ScreenHeading } from '../components/AppShell';
import {
  AppCard,
  EmptyState,
  Eyebrow,
  PrimaryButton,
  SecondaryButton,
  StatusChip,
} from '../components/primitives';
import { useNexusRelationshipStore } from '../../stores/prioritization/nexusRelationshipStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { getItemCountState } from '../../components/collection/types';
import type { NexusRelationshipProposal } from '../../lib/nexusAgentService';

export default function RelationshipsQueueScreen() {
  const app = getAppDefinition('relationships')!;
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);
  const items = useItemsStore((s) => s.items);
  const fetchItems = useItemsStore((s) => s.fetchFromSupabase);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const workspaceId = getCurrentWorkspaceId() ?? '';
  const wsState = useNexusRelationshipStore((s) =>
    workspaceId ? s.byWorkspace[workspaceId] : undefined,
  );
  const fetchProposals = useNexusRelationshipStore((s) => s.fetchProposals);
  const accept = useNexusRelationshipStore((s) => s.accept);
  const dismiss = useNexusRelationshipStore((s) => s.dismiss);

  const proposals = useMemo(() => wsState?.proposals ?? [], [wsState?.proposals]);
  const loading = wsState?.loading ?? false;

  const titleById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) map.set(item.id, item.title);
    return map;
  }, [items]);

  const reload = async () => {
    setActionError(null);
    await fetchItems();
    if (workspaceId) await fetchProposals(workspaceId);
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return proposals;
    return proposals.filter((p) => {
      const source = titleById.get(p.sourceItemId) ?? p.sourceItemId;
      const target = titleById.get(p.targetItemId) ?? p.targetItemId;
      return (
        source.toLowerCase().includes(q) ||
        target.toLowerCase().includes(q) ||
        p.relationshipType.toLowerCase().includes(q) ||
        p.rationale.toLowerCase().includes(q)
      );
    });
  }, [proposals, search, titleById]);

  const showSearch = getItemCountState(proposals.length) === 'many' || search.length > 0;

  const onAccept = async (p: NexusRelationshipProposal) => {
    if (!userId || !workspaceId) return;
    setBusyId(p.id);
    setActionError(null);
    const result = await accept({ proposalId: p.id, userId, workspaceId });
    setBusyId(null);
    if (!result.success) setActionError(result.error ?? 'Accept failed');
  };

  const onDismiss = async (p: NexusRelationshipProposal) => {
    if (!userId || !workspaceId) return;
    setBusyId(p.id);
    setActionError(null);
    const result = await dismiss({ proposalId: p.id, userId, workspaceId });
    setBusyId(null);
    if (!result.success) setActionError(result.error ?? 'Dismiss failed');
  };

  return (
    <AppShell
      app={app}
      actions={
        <SecondaryButton onClick={() => void reload()} disabled={loading} className="!px-3 !py-1.5">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          {loading ? 'Reading…' : 'Refresh'}
        </SecondaryButton>
      }
    >
      <ScreenHeading
        title="Which connections should stand?"
        subtitle="AI-suggested relationships waiting for a human accept or dismiss."
      />
      <BallLine ballWith="you — accept or dismiss suggestions" />

      {showSearch && (
        <div className="relative mb-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search suggestions…"
            className="w-full rounded-md border border-gray-300 bg-white py-2.5 pl-9 pr-3 font-body text-sm dark:border-[var(--rule)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-pri)]"
          />
        </div>
      )}

      {actionError && (
        <p className="mb-3 font-body text-sm text-luminous-coral-700 dark:text-luminous-coral-300">
          {actionError}
        </p>
      )}

      <AppCard>
        <Eyebrow>{filtered.length} AI suggestions</Eyebrow>
        {loading ? (
          <p className="mt-2 font-body text-sm text-gray-500 dark:text-[var(--text-ter)]">
            Reading Nexus proposals…
          </p>
        ) : filtered.length === 0 ? (
          <div className="mt-3">
            <EmptyState
              title="No pending suggestions"
              hint="When Nexus (or another agent) proposes relationships, they show up here for review."
              action={
                <SecondaryButton onClick={() => navigate('/prioritization/relationships')}>
                  Open relationships
                </SecondaryButton>
              }
            />
          </div>
        ) : (
          <ul className="mt-3 space-y-3">
            {filtered.map((p) => {
              const sourceTitle = titleById.get(p.sourceItemId) ?? 'Unknown item';
              const targetTitle = titleById.get(p.targetItemId) ?? 'Unknown item';
              const busy = busyId === p.id;
              return (
                <li
                  key={p.id}
                  className="rounded-lg border border-gray-100 p-4 dark:border-[var(--rule)]"
                >
                  <div className="flex flex-wrap items-center gap-1.5">
                    <StatusChip label="AI-generated" tone="slate" />
                    <StatusChip label={p.relationshipType} tone="neutral" />
                    <StatusChip
                      label={p.confidence}
                      tone={
                        p.confidence === 'high'
                          ? 'green'
                          : p.confidence === 'low'
                            ? 'coral'
                            : 'mustard'
                      }
                    />
                  </div>
                  <p className="mt-2 font-body text-sm text-gray-900 dark:text-[var(--text-pri)]">
                    <Link
                      to={`/apps/relationships/${p.sourceItemId}`}
                      className="font-medium text-primary-700 hover:underline dark:text-primary-300"
                    >
                      {sourceTitle}
                    </Link>
                    <span className="mx-1.5 text-gray-400">→</span>
                    <Link
                      to={`/apps/relationships/${p.targetItemId}`}
                      className="font-medium text-primary-700 hover:underline dark:text-primary-300"
                    >
                      {targetTitle}
                    </Link>
                  </p>
                  {p.rationale && (
                    <p className="mt-2 font-body text-sm text-gray-600 dark:text-[var(--text-sec)]">
                      {p.rationale}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <PrimaryButton
                      onClick={() => void onAccept(p)}
                      disabled={busy || !userId}
                      className="!py-2"
                    >
                      Accept
                    </PrimaryButton>
                    <SecondaryButton
                      onClick={() => void onDismiss(p)}
                      disabled={busy || !userId}
                      className="!py-2"
                    >
                      Dismiss
                    </SecondaryButton>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </AppCard>

      <p className="mt-4 font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
        Full graph:{' '}
        <Link
          to="/prioritization/relationships"
          className="text-primary-600 hover:underline dark:text-primary-400"
        >
          Relationships home
        </Link>
      </p>
    </AppShell>
  );
}
